import { RELATIONSHIP_TYPES } from "@tellescope/constants";
import type { EnduserRelationship, TableInputChoice } from "@tellescope/types-models";
import { useForm } from "../../../use-form";
import { Button, DatePicker, PhoneInput } from "@tellescope/ui";
import { Select, Input } from "@tellescope/design-system";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { RelatedContactCustomField } from "./RelatedContactCustomField";

const convertStringToRelationship = (str: string): EnduserRelationship["type"] | undefined =>
  RELATIONSHIP_TYPES.find((type) => type === str);

export interface RelatedContactFormResult {
  fname: string;
  lname?: string;
  relationship: EnduserRelationship["type"];
  dateOfBirth?: Date;
  email: string;
  phoneNumber?: string;
  fields?: Record<string, string>;
}

const DEFAULT_FIELD_CONFIG_NAMES = {
  fname: "First Name",
  lname: "Last Name",
  dateOfBirth: "Date of Birth",
  email: "Email",
  phoneNumber: "Phone Number",
} as const;

export interface AddRelatedContactFormProps {
  initialContact?: RelatedContactFormResult;
  onSave: (contact: RelatedContactFormResult) => void;
  onCancel?: () => void;
  className?: string;
  onBlur?: () => void;
  hasSavedContact?: boolean;
  relatedContactTypes?: string[];
  hiddenDefaultFields?: string[];
  tableChoices?: TableInputChoice[];
}

export const AddRelatedContactForm = ({
  initialContact,
  onSave,
  onCancel,
  className,
  onBlur,
  hasSavedContact,
  relatedContactTypes,
  hiddenDefaultFields,
  tableChoices,
}: AddRelatedContactFormProps) => {
  const { t } = useTranslation("forms");
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>(initialContact?.fields ?? {});

  const allowedRelationships = relatedContactTypes?.length
    ? RELATIONSHIP_TYPES.filter((type) => relatedContactTypes.includes(type))
    : RELATIONSHIP_TYPES;
  const onlyAllowedRelationship = allowedRelationships.length === 1 ? allowedRelationships[0] : undefined;

  const form = useForm({
    fields: {
      fname: {
        type: "text",
        initialValue: initialContact?.fname,
      },
      lname: {
        type: "text",
        initialValue: initialContact?.lname,
      },
      relationship: {
        type: "select",
        initialValue: initialContact?.relationship ?? onlyAllowedRelationship ?? "",
        required: true,
      },
      dateOfBirth: {
        type: "date",
        initialValue: initialContact?.dateOfBirth,
      },
      email: {
        type: "email",
        initialValue: initialContact?.email,
      },
      phoneNumber: {
        type: "phone",
        initialValue: initialContact?.phoneNumber,
      },
    },
    validateOn: "blur",
  });

  const hasAnyEnteredField =
    Object.values(form.values).some((value) => !!value) || Object.values(customFieldValues).some((value) => !!value);

  const reportBlurIfContactSaved = () => {
    if (hasSavedContact) onBlur?.();
  };

  const getFieldProps: typeof form.getFieldProps = (fieldName) => {
    const props = form.getFieldProps(fieldName);
    return {
      ...props,
      onBlur: () => {
        props.onBlur();
        reportBlurIfContactSaved();
      },
    };
  };

  const getCustomFieldProps: typeof form.getCustomFieldProps = (fieldName) => {
    const props = form.getCustomFieldProps(fieldName);
    return {
      ...props,
      onBlur: () => {
        props.onBlur();
        reportBlurIfContactSaved();
      },
    };
  };

  const { onValueChange: onPhoneChange, ...phoneProps } = getCustomFieldProps("phoneNumber");

  const isHidden = (fieldName: keyof typeof DEFAULT_FIELD_CONFIG_NAMES) =>
    !!hiddenDefaultFields?.includes(DEFAULT_FIELD_CONFIG_NAMES[fieldName]);

  return (
    <div className="grid grid-cols-2 gap-2" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
      {!isHidden("fname") && <Input {...getFieldProps("fname")} placeholder={t("First Name")} />}
      {!isHidden("lname") && <Input {...getFieldProps("lname")} placeholder={t("Last Name")} />}
      <Select {...getCustomFieldProps("relationship")} disabled={allowedRelationships.length === 1}>
        <Select.Trigger className={className}>
          <Select.Value placeholder={t("Relationship")}>{form.values.relationship}</Select.Value>
        </Select.Trigger>
        <Select.Content>
          {allowedRelationships.map((relationship) => (
            <Select.Item key={relationship} value={relationship} keywords={[relationship]}>
              {relationship}
            </Select.Item>
          ))}
        </Select.Content>
      </Select>
      {!isHidden("dateOfBirth") && (
        <DatePicker {...getCustomFieldProps("dateOfBirth")} placeholder={t("Date of Birth")} label="" />
      )}
      {!isHidden("email") && <Input {...getFieldProps("email")} type="email" placeholder={t("Email")} />}
      {!isHidden("phoneNumber") && (
        <PhoneInput {...phoneProps} onChange={onPhoneChange} placeholder={t("Phone Number")} />
      )}
      {tableChoices?.map((choice) => (
        <RelatedContactCustomField
          key={choice.label}
          choice={choice}
          value={customFieldValues[choice.label] ?? ""}
          onChange={(newValue) => setCustomFieldValues((prev) => ({ ...prev, [choice.label]: newValue }))}
          onBlur={reportBlurIfContactSaved}
        />
      ))}
      <div className="flex col-span-3 justify-end gap-2">
        {!!onCancel && (
          <Button
            variant="outline"
            onClick={() => {
              onCancel();
            }}
          >
            {t("Cancel")}
          </Button>
        )}
        <Button
          disabled={!form.isValid || !hasAnyEnteredField}
          onClick={() => {
            const relationship = convertStringToRelationship(form.values.relationship);
            if (!relationship) return;

            onSave({
              ...form.values,
              relationship,
              ...(tableChoices?.length ? { fields: customFieldValues } : {}),
            });
          }}
        >
          {t("Save Contact")}
        </Button>
      </div>
    </div>
  );
};
